/**
 * Authentication hook
 * Reads from sessionStorage (shared with existing jQuery site)
 * Validates JWT token expiry and provides auth utilities
 */

import { useState, useEffect } from 'react';
import type { AuthUser } from '../types/models';
import { clearAuthStorage } from '../utils/authStorage';

interface DecodedToken {
  exp: number;  // Expiry timestamp (seconds since epoch)
  sub: string;  // Cognito sub
  email?: string;
}

const base64UrlToBase64 = (input: string): string => {
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padLength = (4 - (base64.length % 4)) % 4;
  return base64 + '='.repeat(padLength);
};

// Decode JWT token (simplified - no signature verification needed client-side)
const decodeToken = (token: string): DecodedToken | null => {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;

    const base64 = base64UrlToBase64(parts[1]);
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
    const jsonPayload = new TextDecoder().decode(bytes);
    return JSON.parse(jsonPayload) as DecodedToken;
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
};

// Check if token is valid and not expired
const isTokenValid = (idToken: string | null): boolean => {
  if (!idToken) return false;
  
  const decoded = decodeToken(idToken);
  if (!decoded) return false;
  
  const now = Math.floor(Date.now() / 1000);
  const expiresIn = decoded.exp - now;
  
  // Token expired — only reject when it is actually past expiry.
  // Do NOT pre-emptively mark it invalid 60 s early: that creates a window
  // where the token is still accepted by the API but the app treats it as dead.
  // The silent-refresh path in useInactivityLogout handles renewing before
  // expiry, so a few stray seconds don't matter here.
  if (expiresIn <= 0) {
    console.warn('Token has expired.');
    return false;
  }
  
  return true;
};

export const useAuth = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Read auth data from sessionStorage (same as jQuery version)
    const tenantId = localStorage.getItem('tenantId');
    const userId = localStorage.getItem('userId');
    const email = localStorage.getItem('userEmail');
    const name = localStorage.getItem('userName');
    const role = localStorage.getItem('userRole');
    const orgName = localStorage.getItem('orgName');
    const idToken = localStorage.getItem('idToken');
    const plan = localStorage.getItem('userPlan') || 'basic';

    // Validate token expiry
    if (tenantId && userId && isTokenValid(idToken)) {
      setUser({
        userId,
        tenantId,
        email: email || '',
        name: name || '',
        role: role || 'user',
        orgName: orgName || '',
        plan,
      });
      setIsAuthenticated(true);
    } else {
      // Token absent or expired — just mark unauthenticated.
      // Do NOT clear storage here: useInactivityLogout handles refresh + eventual
      // logout in a single place (App.tsx). Clearing storage synchronously on every
      // component mount that calls useAuth() would kill a refresh that may be
      // in-flight and cause cascade logouts across the 12 call-sites.
      setIsAuthenticated(false);
    }

    setIsLoading(false);
  }, []);

  const logout = () => {
    const redirectPath = window.location.pathname + window.location.search || '/dashboard';

    // Clear auth storage (localStorage auth keys + sessionStorage)
    clearAuthStorage();
    setUser(null);
    setIsAuthenticated(false);

    // Preserve requested location so login can send user back
    try {
      sessionStorage.setItem('redirectAfterLogin', redirectPath);
    } catch {
      // ignore
    }

    // Redirect to login
    window.location.href = '/login';
  };

  const getTenantId = () => localStorage.getItem('tenantId') || '';
  const getUserId = () => localStorage.getItem('userId') || '';

  return {
    user,
    isAuthenticated,
    isLoading,
    logout,
    getTenantId,
    getUserId,
  };
};

